// Copyright (c) Move Closer 2022.

import { Model } from '@movecloser/front-core'

import { CartCoupon, CartData, CartDiscount, CartItemData, CartTax } from '../../../contexts'

import { ICart, ICartItem } from '../contracts'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export class Cart extends Model<CartData> implements ICart {
  protected boot (): void {
    this.initialValues = {}

    this.modelProperties = [
      'coupons',
      'billingAddress',
      'discounts',
      'email',
      'id',
      'items',
      'note',
      'selectedPaymentMethod',
      'selectedPaymentMethodId',
      'selectedShippingMethod',
      'shippingAddress',
      'subtotal',
      'taxes',
      'total'
    ]
  }

  public getCoupons (): CartCoupon[] {
    if (!Object.prototype.hasOwnProperty.call(this._data, 'coupons')) {
      return []
    }

    return this._data.coupons
  }

  public getFreeDeliveryFrom (): number {
    return 99
  }

  public getTotalPrice (): number {
    if (
      !Object.prototype.hasOwnProperty.call(this._data, 'total') ||
      !this._data.total
    ) {
      return 0
    }

    return this._data.total.value
  }

  public getDiscounts (): CartDiscount[] {
    if (!Object.prototype.hasOwnProperty.call(this._data, 'discounts')) {
      return []
    }

    return this._data.discounts
  }

  public getTaxes (): CartTax[] {
    if (!Object.prototype.hasOwnProperty.call(this._data, 'taxes')) {
      return []
    }

    return this._data.taxes
  }

  public getTotalTax (): number {
    if (!Object.prototype.hasOwnProperty.call(this._data, 'taxes')) {
      return 0
    }

    return this._data.taxes.reduce((acc: number, nextTax: CartTax) => {
      return acc + nextTax.amount.value
    }, 0)
  }

  public getTotalQuantity (): number {
    if (!Object.prototype.hasOwnProperty.call(this._data, 'items')) {
      return 0
    }

    return this._data.items.reduce((acc: number, next: CartItemData) => {
      return acc + next.quantity
    }, 0)
  }

  public getItems (): CartItemData[] {
    if (!Object.prototype.hasOwnProperty.call(this._data, 'items')) {
      return []
    }

    return this._data.items
  }

  public getShippingPrice (): number {
    if (!Object.prototype.hasOwnProperty.call(this._data, 'selectedShippingMethod') || !this._data.selectedShippingMethod) {
      return 0
    }

    return this._data.selectedShippingMethod.price.value
  }

  public getSubtotalPrice (): number {
    if (!Object.prototype.hasOwnProperty.call(this._data, 'subtotal')) {
      return 0
    }

    return this._data.subtotal.value
  }

  public getTotalDiscount (): number {
    if (!Object.prototype.hasOwnProperty.call(this._data, 'discounts')) {
      return 0
    }

    return this._data.discounts?.reduce((acc: number, nextDiscount: CartDiscount) => {
      return acc + nextDiscount.amount.value
    }, 0)
  }

  public hasInvoice (): boolean {
    const billingAddress = this._data.billingAddress

    return !!billingAddress && !!billingAddress.vatId && !!billingAddress.company
  }

  public isSet (): boolean {
    return !!this._data.id
  }

  public isSameBillingAddress (): boolean {
    const billingAddress = this._data.billingAddress
    const shippingAddress = this._data.shippingAddress

    if (!billingAddress || !shippingAddress || !billingAddress.street || !shippingAddress.street ||
      !billingAddress.street.length || !shippingAddress.street.length) {
      return false
    }

    return billingAddress.street[0] === shippingAddress.street[0] &&
      billingAddress.city === shippingAddress.city &&
      billingAddress.postcode === shippingAddress.postcode
  }

  // protected relatesToItems (values: ModelPayload[]) {
  //   return this.hasMany(CartItem, values)
  // }
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export class CartItem extends Model<CartItemData> implements ICartItem {
  protected boot (): void {
    this.initialValues = {}
    this.modelProperties = ['appliedCoupons', 'product', 'quantity', 'prices', 'outOfStock', 'uid']
  }

  public get isGift (): boolean {
    const keywords = ['gratis', 'free']

    const toTest: string = this._data.product ? this._data.product.variants[0].sku.toLowerCase() : ''

    for (const k of keywords) {
      if ((new RegExp(`/.*(${k}).*/`)).test(toTest)) {
        return true
      }
    }

    return false
  }
}
